import { useCallback, useEffect, useRef } from 'react';

import { buildButtonPropsForPersistence, readButtonConfig } from '@/lib/blocks/button-links';
import { usePageEditorStore } from '@/stores/page-editor-store';
import type { PageBlock } from '@/types/block';
import type { ButtonConfig } from '@/types/button';

export const BUTTON_PREVIEW_DEBOUNCE_MS = 300;

interface UseBlockButtonDraftSyncOptions {
  sectionId: string;
  block: PageBlock;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function useBlockButtonDraftSync({
  sectionId,
  block,
  open,
  onOpenChange,
}: UseBlockButtonDraftSyncOptions) {
  const updateBlock = usePageEditorStore((state) => state.updateBlock);
  const updateBlockPreview = usePageEditorStore((state) => state.updateBlockPreview);
  const originalPropsRef = useRef<Record<string, unknown> | undefined>(undefined);
  const didSaveRef = useRef(false);
  const previewTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPendingPreviewUpdate = useCallback(() => {
    if (previewTimeoutRef.current !== null) {
      clearTimeout(previewTimeoutRef.current);
      previewTimeoutRef.current = null;
    }
  }, []);

  const schedulePreviewUpdate = useCallback(
    (nextProps: Record<string, unknown>) => {
      clearPendingPreviewUpdate();
      previewTimeoutRef.current = setTimeout(() => {
        updateBlockPreview(sectionId, block.id, { props: nextProps });
        previewTimeoutRef.current = null;
      }, BUTTON_PREVIEW_DEBOUNCE_MS);
    },
    [block.id, clearPendingPreviewUpdate, sectionId, updateBlockPreview],
  );

  useEffect(() => {
    if (!sectionId || !block.id) {
      return;
    }

    if (open && originalPropsRef.current === undefined) {
      originalPropsRef.current = block.props ? { ...block.props } : undefined;
      didSaveRef.current = false;
      return;
    }

    if (!open) {
      clearPendingPreviewUpdate();
      originalPropsRef.current = undefined;
      didSaveRef.current = false;
    }
  }, [block.id, block.props, clearPendingPreviewUpdate, open, sectionId]);

  const getOriginalProps = useCallback(
    () => originalPropsRef.current ?? block.props,
    [block.props],
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        clearPendingPreviewUpdate();
        originalPropsRef.current = block.props ? { ...block.props } : undefined;
        didSaveRef.current = false;
      } else {
        clearPendingPreviewUpdate();
        if (!didSaveRef.current) {
          updateBlockPreview(sectionId, block.id, { props: originalPropsRef.current });
        }
        originalPropsRef.current = undefined;
        didSaveRef.current = false;
      }

      onOpenChange(nextOpen);
    },
    [block.id, block.props, clearPendingPreviewUpdate, onOpenChange, sectionId, updateBlockPreview],
  );

  useEffect(
    () => () => {
      clearPendingPreviewUpdate();
    },
    [clearPendingPreviewUpdate],
  );

  const handleDraftChange = useCallback(
    (value: ButtonConfig) => {
      const nextProps = buildButtonPropsForPersistence(getOriginalProps(), value);
      schedulePreviewUpdate(nextProps);
    },
    [getOriginalProps, schedulePreviewUpdate],
  );

  const handleDraftSave = useCallback(
    async (value: ButtonConfig) => {
      const originalProps = getOriginalProps();
      const nextProps = buildButtonPropsForPersistence(originalProps, value);
      clearPendingPreviewUpdate();
      // Reset to original so withHistory snapshots pre-edit state.
      updateBlockPreview(sectionId, block.id, { props: originalProps });
      didSaveRef.current = true;
      updateBlock(sectionId, block.id, { props: nextProps });
    },
    [
      block.id,
      clearPendingPreviewUpdate,
      getOriginalProps,
      sectionId,
      updateBlock,
      updateBlockPreview,
    ],
  );

  return {
    initialValue: readButtonConfig(block.props),
    handleDraftChange,
    handleDraftSave,
    handleOpenChange,
  };
}
